import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { MessageTaskGroup, Task } from 'js-sdk/src/types/Tasks';

export type TasksSliceState = {
  /**
   * Tasks created (or mutated) in this session, keyed by their source
   * `message_id`. Lets a message row render its task card immediately.
   *
   * NOTE: this is session-local only. There is no task-svc endpoint yet to
   * hydrate existing tasks per message on load — see `useMessageTask` and the
   * PR "Backend dependencies" section.
   */
  linkedTasksByMessageId: Record<string, Task>;
  selectedTask: Task | null;
  /** Opaque keyset cursor for the next page, keyed by `${organizationId}:${filter}`. `null` means no more pages. */
  nextCursorByKey: Record<string, string | null>;
  /**
   * Caller's tasks per conversation from `POST /tasks/messages`, keyed by
   * `${organizationId}:${counterPartyType}:${counterPartyId}`, then message id.
   * Kept separate from the org-wide task list above.
   */
  conversationTasksByKey: Record<string, Record<string, Task[]>>;
  /** Conversation currently open; newly created tasks are added under it. */
  activeConversationKey: string | null;
  /**
   * In-flight `POST /tasks/messages` requests per conversation key, counted per message id.
   * `ALL_MESSAGES` counts whole-conversation requests (no message_ids).
   */
  pendingConversationFetchesByKey: Record<string, Record<string, number>>;
};

export const ALL_MESSAGES = '*';

const initialState: TasksSliceState = {
  linkedTasksByMessageId: {},
  selectedTask: null,
  nextCursorByKey: {},
  conversationTasksByKey: {},
  activeConversationKey: null,
  pendingConversationFetchesByKey: {},
};

/** Replaces `task` in every conversation map that holds it. Returns whether it was found. */
function replaceTask(state: TasksSliceState, task: Task): boolean {
  const messageId = task.source_ref?.message_id;
  if (!messageId) return false;
  let found = false;
  for (const byMessage of Object.values(state.conversationTasksByKey)) {
    const index = byMessage[messageId]?.findIndex((t) => t.id === task.id) ?? -1;
    if (index !== -1) {
      byMessage[messageId][index] = task;
      found = true;
    }
  }
  return found;
}

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setLinkedTask: (state, action: PayloadAction<Task>) => {
      const task = action.payload;
      if (task.source_type === 'message' && task.source_ref?.message_id) {
        state.linkedTasksByMessageId[task.source_ref.message_id] = task;
      }
    },
    clearLinkedTask: (state, action: PayloadAction<{ messageId: string }>) => {
      delete state.linkedTasksByMessageId[action.payload.messageId];
    },
    setSelectedTask: (state, action: PayloadAction<Task | null>) => {
      state.selectedTask = action.payload;
    },
    updateSelectedTask: (state, action: PayloadAction<Partial<Task>>) => {
      if (state.selectedTask) {
        state.selectedTask = { ...state.selectedTask, ...action.payload };
      }
    },
    setNextCursor: (state, action: PayloadAction<{ key: string; nextCursor: string | null }>) => {
      state.nextCursorByKey[action.payload.key] = action.payload.nextCursor;
    },
    setActiveConversationKey: (state, action: PayloadAction<string | null>) => {
      state.activeConversationKey = action.payload;
    },
    mergeConversationTasks: (
      state,
      action: PayloadAction<{ key: string; groups: MessageTaskGroup[] }>
    ) => {
      const byMessage = (state.conversationTasksByKey[action.payload.key] ??= {});
      for (const { message_id, tasks } of action.payload.groups) {
        const existing = byMessage[message_id] ?? [];
        const incomingIds = new Set(tasks.map((t) => t.id));
        byMessage[message_id] = [...existing.filter((t) => !incomingIds.has(t.id)), ...tasks];
      }
    },
    conversationFetchStarted: (
      state,
      action: PayloadAction<{ key: string; messageIds?: string[] }>
    ) => {
      const { key, messageIds = [ALL_MESSAGES] } = action.payload;
      const pending = (state.pendingConversationFetchesByKey[key] ??= {});
      for (const id of messageIds) pending[id] = (pending[id] ?? 0) + 1;
    },
    conversationFetchEnded: (
      state,
      action: PayloadAction<{ key: string; messageIds?: string[] }>
    ) => {
      const { key, messageIds = [ALL_MESSAGES] } = action.payload;
      const pending = state.pendingConversationFetchesByKey[key];
      if (!pending) return;
      for (const id of messageIds) {
        if ((pending[id] ?? 0) <= 1) delete pending[id];
        else pending[id] -= 1;
      }
      if (Object.keys(pending).length === 0) delete state.pendingConversationFetchesByKey[key];
    },
    clearConversationTasks: (state, action: PayloadAction<string>) => {
      delete state.conversationTasksByKey[action.payload];
    },
    // Replaces the task wherever it is stored. Use for complete/reopen/update, which may come from
    // the Tasks list for a message in another conversation: never adds a missing task.
    replaceConversationTask: (state, action: PayloadAction<Task>) => {
      replaceTask(state, action.payload);
    },
    // After create: replaces the task wherever it is stored, otherwise adds it to the open
    // conversation (creation always originates from a message in the open conversation).
    upsertConversationTask: (state, action: PayloadAction<Task>) => {
      const task = action.payload;
      const messageId = task.source_ref?.message_id;
      if (!messageId || replaceTask(state, task) || !state.activeConversationKey) return;
      const byMessage = (state.conversationTasksByKey[state.activeConversationKey] ??= {});
      byMessage[messageId] = [...(byMessage[messageId] ?? []), task];
    },
  },
});

export { tasksSlice };
